import { useEffect, useState } from 'react'
import type { Listing } from '../api/store'
import { fetchListings } from '../api/store'
import type { StoreFilters } from '../types/store'
import { serializeStoreFilterParams } from '../types/store'

export function useStore(query: string, opts?: { sort?: 'popular' | 'newest'; limit?: number; filters?: Partial<StoreFilters> }) {
  const [items, setItems] = useState<Listing[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const t = setTimeout(async () => {
      try {
        // Prefer server-side filtering/sorting when filters are provided
        const params: Record<string, any> = serializeStoreFilterParams(opts?.filters as any)
        const q = query.trim()
        if (q) params.q = q
        let arr = await fetchListings(params)

        // Client-side filtering fallback (match mobile behavior) in case backend ignores params
        const f = (opts?.filters ?? {}) as StoreFilters
        const toNum = (v: any): number | null => {
          if (v == null) return null
          const n = typeof v === 'number' ? v : parseFloat(String(v))
          return Number.isFinite(n) ? n : null
        }
        const ql = (params.q || '').toString().trim().toLowerCase()
        const cat = (f.category || '').toString().trim().toLowerCase()
        const cond = f.condition || null
        const inStockOnly = Boolean(f.inStockOnly)
        const pMin = toNum(f.priceMin)
        const pMax = toNum(f.priceMax)

        let filtered = arr.slice()
        if (ql) {
          filtered = filtered.filter((l: any) => {
            const title = l?.product?.title?.toLowerCase?.() || ''
            const subtitle = l?.product?.subtitle?.toLowerCase?.() || ''
            const catName = l?.product?.category?.name?.toLowerCase?.() || ''
            return title.includes(ql) || subtitle.includes(ql) || catName.includes(ql)
          })
        }
        if (cat) {
          filtered = filtered.filter((l: any) => {
            const v = (l?.product?.category?.slug || l?.product?.category?.name || '').toLowerCase()
            return v ? v.includes(cat) : false
          })
        }
        if (cond) {
          filtered = filtered.filter((l: any) => (l?.condition || '').toLowerCase() === cond)
        }
        if (inStockOnly) {
          filtered = filtered.filter((l: any) => {
            const stock = typeof l.stock_qty === 'number' ? l.stock_qty : null
            const reserved = typeof l.reserved_qty === 'number' ? l.reserved_qty : 0
            if (stock == null) return false
            return stock - reserved > 0
          })
        }
        if (pMin != null || pMax != null) {
          filtered = filtered.filter((l: any) => {
            const price = toNum(l?.price_pixe)
            if (price == null) return false
            if (pMin != null && price < pMin) return false
            if (pMax != null && price > pMax) return false
            return true
          })
        }

        // Sorting fallback for all sorts to ensure effect
        const finalSort = (f as any)?.sort ?? opts?.sort
        if (finalSort === 'popular') {
          filtered.sort((a: any, b: any) => (b?.likes_count || 0) - (a?.likes_count || 0))
        } else if (finalSort === 'price_asc') {
          filtered.sort((a: any, b: any) => {
            const A = toNum(a?.price_pixe) ?? Number.MAX_SAFE_INTEGER
            const B = toNum(b?.price_pixe) ?? Number.MAX_SAFE_INTEGER
            return A - B
          })
        } else if (finalSort === 'price_desc') {
          filtered.sort((a: any, b: any) => {
            const A = toNum(a?.price_pixe) ?? Number.MIN_SAFE_INTEGER
            const B = toNum(b?.price_pixe) ?? Number.MIN_SAFE_INTEGER
            return B - A
          })
        }

        arr = filtered
        if (typeof opts?.limit === 'number') arr = arr.slice(0, Math.max(0, opts.limit))
        if (!cancelled) setItems(arr)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load listings')
      }
    }, 300)
    return () => { cancelled = true; clearTimeout(t) }
  }, [query, opts?.sort, opts?.limit, JSON.stringify(opts?.filters ?? {})])

  return { items, error }
}
